import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { UserLayoutDto } from '@myarvato/core/customization';
import { TransportViewDto } from '@myarvato/modules/transport/models';
import { KpiService } from '@myarvato/modules/transport/kpis';
import { LocalizedDecimalPipe } from '@myarvato/shared/localized-decimal-pipe';
import { RelativeDateService } from '@myarvato/shared/calendar';
import { Router } from '@angular/router';
import { UserTrackingService } from '@myarvato/core/services';
import { Skeleton } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'tr[transport-saved-view]',
  imports: [LocalizedDecimalPipe, Skeleton, ButtonModule],
  templateUrl: './saved-view.component.html',
  styleUrl: './saved-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedViewComponent implements OnInit {
  // Inputs
  public readonly savedView = input.required<UserLayoutDto<TransportViewDto>>();

  // Injections
  private readonly kpiService = inject(KpiService);
  private readonly relativeDateService = inject(RelativeDateService);
  private readonly router = inject(Router);
  private readonly userTrackingService = inject(UserTrackingService);

  // Fields
  protected readonly trackingCategory = 'Transport - Overview - Saved View';

  // Change detection
  protected readonly state = signal<{
    loading: boolean;
    error: unknown | undefined;
    data: number | string | undefined;
  }>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  protected readonly isOnError = computed(() => this.state().error !== undefined);
  protected readonly isSuccess = computed(() => this.state().data !== undefined && !this.isOnError());
  protected readonly isLoading = computed(() => this.state().loading);
  protected readonly filterHits = computed(() => this.state().data);

  public ngOnInit(): void {
    if (this.savedView()?.value) {
      this.fetchHits();
    } else {
      this.state.set({ loading: false, error: undefined, data: '0' });
    }
  }

  private fetchHits(): void {
    this.state.set({ loading: true, error: undefined, data: undefined });

    const filters = this.relativeDateService.updatedRelativeDateFilters(this.savedView()?.value?.filters);

    this.kpiService
      .getKpis({
        first: undefined,
        rows: undefined,
        sortField: undefined,
        sortOrder: 1,
        filters: filters,
        globalFilter: null,
        multiSortMeta: null,
      })
      .subscribe({
        next: (data) => {
          this.state.set({ loading: false, error: undefined, data: data.kpis[0].value });
        },
        error: (error) => {
          console.error(error);
          this.state.set({ loading: false, error: error, data: undefined });
        },
      });
  }

  protected goToSavedView(event: UserLayoutDto<TransportViewDto>) {
    this.userTrackingService.trackEvent(this.trackingCategory, 'Open View clicked');

    this.router
      .navigate(['transport', 'list'], {
        state: { 'selected-view': event },
      })
      .then();
  }
}
